import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/config/api";
import { Plus, X, ArrowLeft, User, ChevronDown, ChevronUp } from "lucide-react";
import { CURRENCIES } from "@/utils/currencies";
import {
  validateName,
  validateEmail,
  validateDueDate,
  validateDateRange,
  onlyNumericInput,
  onlyNameInput,
} from "@/utils/validation";
import { toLocalDateInputValue } from "@/utils/date";

const PAYMENT_EXTRA_FIELDS = {
  cheque: [{ key: "cheque_number", label: "Cheque Number", type: "text" }],
  lpo: [{ key: "lpo_number", label: "LPO Number", type: "text" }],
  visa: [
    { key: "card_authorization", label: "Card Authorization", type: "text" },
  ],
  bank_transfer: [
    { key: "bank_name", label: "Bank Name", type: "text" },
    { key: "reference", label: "Reference / Transaction ID", type: "text" },
  ],
  credit: [{ key: "reference", label: "Reference", type: "text" }],
  cash: [],
};

const ENTITY_TYPES = [
  { value: "applicant", label: "Applicant" },
  { value: "room", label: "Room" },
  { value: "vehicle", label: "Vehicle" },
  { value: "guide", label: "Guide" },
];

const VENDOR_CATEGORIES = [
  { id: "hotel", label: "Hotel" },
  { id: "travel_agency", label: "Travel Agency" },
  { id: "insurance", label: "Insurance" },
  { id: "car_rental", label: "Car Rental" },
  { id: "visa_agent", label: "Visa Agent" },
  { id: "tour_operator", label: "Tour Operator" },
  { id: "other", label: "Other" },
];

function ServiceEntitiesBlock({
  orderId,
  serviceId,
  entities,
  onAdd,
  onRemove,
}) {
  const [entityType, setEntityType] = useState("applicant");
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    setSubmitting(true);
    try {
      const created = await api.orders.serviceEntities.create(
        orderId,
        serviceId,
        {
          entity_type: entityType,
          quantity: quantity || 1,
        },
      );
      onAdd(created);
      setQuantity(1);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entityId) => {
    try {
      await api.orders.serviceEntities.delete(orderId, serviceId, entityId);
      onRemove(entityId);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="pt-2 border-t border-stone-200">
      <label className="block text-xs text-stone-600 mb-2">
        Service entities (rooms, vehicles, applicants, guides)
      </label>
      {entities.length > 0 && (
        <ul className="text-sm text-stone-700 mb-2 space-y-1">
          {entities.map((e) => (
            <li key={e.id} className="flex items-center gap-2">
              <span>
                {e.entity_type} × {e.quantity}
              </span>
              <button
                type="button"
                onClick={() => handleDelete(e.id)}
                className="text-red-600 hover:underline text-xs"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="px-2 py-1.5 text-sm border border-stone-300 rounded-lg"
        >
          {ENTITY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
          className="w-16 px-2 py-1.5 text-sm border border-stone-300 rounded-lg"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={submitting}
          className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg"
        >
          {submitting ? "Adding…" : "Add"}
        </button>
      </div>
    </div>
  );
}

function ChipMultiValueInput({
  values,
  onChange,
  placeholder,
  helperText,
  splitBy = "comma", // "comma" | "newline" | "none"
  commitOnComma = false,
}) {
  const [draft, setDraft] = useState("");

  const commitFromRaw = (raw) => {
    const str = String(raw || "");
    let parts = [];
    if (splitBy === "comma") {
      parts = str.split(",").map((s) => s.trim()).filter(Boolean);
    } else if (splitBy === "newline") {
      parts = str
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
    } else {
      const v = str.trim();
      parts = v ? [v] : [];
    }
    if (!parts.length) return false;
    onChange([...(values || []), ...parts]);
    setDraft("");
    return true;
  };

  return (
    <div className="space-y-2">
      {helperText && <p className="form-helper mb-1">{helperText}</p>}
      <div className="flex flex-wrap gap-1">
        {(values || []).length ? (
          (values || []).map((v, idx) => (
            <span
              key={`${idx}-${v}`}
              className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[12px] text-stone-700"
            >
              <span>{v}</span>
              <button
                type="button"
                onClick={() =>
                  onChange((values || []).filter((_, i) => i !== idx))
                }
                className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                aria-label="Remove"
              >
                <X size={14} />
              </button>
            </span>
          ))
        ) : (
          <span className="text-xs text-stone-400">No values yet.</span>
        )}
      </div>
      <div className="flex gap-2 min-w-0">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder || "Type values and press Enter"}
          className="flex-1 px-2 py-1.5 text-sm border border-stone-300 rounded-lg"
          onKeyDown={(e) => {
            if (
              e.key === "Backspace" &&
              !String(draft || "").trim() &&
              (values || []).length > 0
            ) {
              // Backspace on an empty draft removes the last chip.
              e.preventDefault();
              onChange(values.slice(0, -1));
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              commitFromRaw(draft);
            }
            if (commitOnComma && e.key === ",") {
              // Commit when user presses comma (comma acts as delimiter)
              // Append comma so splitBy="comma" can safely finalize last token.
              e.preventDefault();
              commitFromRaw(`${draft},`);
            }
          }}
        />
        <button
          type="button"
          onClick={() => commitFromRaw(draft)}
          disabled={!String(draft || "").trim()}
          className="px-3 py-1.5 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Add
        </button>
      </div>
    </div>
  );
}

export default function NewOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id: orderId } = useParams();
  const isEdit = !!orderId;
  const isInvoiceForm = location.pathname === "/invoices/new";
  const parentInvoiceId = searchParams.get("parent_invoice_id") || "";
  const preselectedCustomerId = searchParams.get("customer_id");
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceTypeVendorCategory, setServiceTypeVendorCategory] = useState(
    {},
  );
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    company: "",
    opening_balance: "",
  });
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [vendorForServiceIndex, setVendorForServiceIndex] = useState(null);
  const [vendorForServiceVendorLine, setVendorForServiceVendorLine] = useState(
    null,
  );
  const [newVendorForm, setNewVendorForm] = useState({
    name: "",
    category: "other",
    opening_balance: "",
  });
  const [addVendorError, setAddVendorError] = useState("");

  const [formData, setFormData] = useState({
    customer_id: preselectedCustomerId || "",
    payment_type: "",
    payment_details: {},
    currency: "OMR",
    due_date: "",
    notes: "",
    vat_percentage: 5.0,
    discount: 0,
  });

  const [passengers, setPassengers] = useState([]);

  const [services, setServices] = useState([
    {
      service_type: "",
      name: "",
      vendor_id: "",
      vendor_name: "",
      cost: "",
      sale_amount: "",
      vendors: [],
      vat_applicable: true,
      extra_fields: {},
      passenger_assignments: [],
    },
  ]);
  const [expandedServiceIndices, setExpandedServiceIndices] = useState(
    new Set([0]),
  );

  useEffect(() => {
    Promise.all([
      api.customers.getAll(),
      api.orders.getServiceTypes(),
      api.vendors.getAll(),
      api.settings.getCompany().catch(() => ({ currency: "OMR" })),
    ])
      .then(([custs, types, vends, settings]) => {
        setCustomers(custs);
        setServiceTypes(types.service_types || []);
        setServiceTypeVendorCategory(types.service_type_vendor_category || {});
        setPaymentTypes(types.payment_types || []);
        setVendors(vends || []);
        setFormData((prev) => ({
          ...prev,
          currency: settings?.currency || "OMR",
          vat_percentage:
            prev.vat_percentage ??
            (settings?.vat_rate != null
              ? parseFloat(String(settings.vat_rate), 10)
              : 5),
        }));
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load data");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (preselectedCustomerId && !isEdit) {
      setFormData((prev) => ({ ...prev, customer_id: preselectedCustomerId }));
    }
  }, [preselectedCustomerId, isEdit]);

  useEffect(() => {
    if (isInvoiceForm && parentInvoiceId) {
      api.invoices
        .getById(parentInvoiceId)
        .then((inv) => {
          setFormData((p) => ({
            ...p,
            customer_id: inv.customer_id || p.customer_id,
            currency: inv.currency || p.currency,
            vat_percentage:
              parseFloat(String(inv.vat_percentage), 10) || p.vat_percentage,
            discount: parseFloat(String(inv.discount ?? 0), 10) || 0,
            due_date: inv.due_date
              ? String(inv.due_date).slice(0, 10)
              : p.due_date,
          }));
        })
        .catch(() => {});
    }
  }, [isInvoiceForm, parentInvoiceId]);

  useEffect(() => {
    if (isEdit && !isInvoiceForm && orderId && serviceTypes.length > 0) {
      api.orders
        .getById(orderId)
        .then((o) => {
          if (o.status !== "open") {
            setError("Only open orders can be edited");
            return;
          }
          setFormData({
            customer_id: o.customer_id,
            payment_type: o.payment_type || "",
            payment_details: o.payment_details || {},
            currency: o.currency || "OMR",
            due_date: o.due_date ? o.due_date.slice(0, 10) : "",
            notes: o.notes || "",
          });
          const orderServices = (o.services || []).map((s) => ({
            id: s.id,
            service_type: s.service_type || "",
            name: s.name || "",
            vendor_id: s.vendor_id || "",
            vendor_name: s.vendor_name || "",
            cost: s.cost != null ? s.cost : "",
            sale_amount: s.sale_amount != null ? s.sale_amount : "",
            vendors: Array.isArray(s.vendors)
              ? s.vendors.map((v) => ({
                  ...v,
                  extra_fields: v.extra_fields || {},
                }))
              : [],
            vat_applicable: s.vat_applicable !== false,
            extra_fields: s.extra_fields || {},
            passenger_assignments: (s.passenger_assignments || []).map((a) => ({
              passenger_id: a.passenger_id,
              role: a.role || "adult",
            })),
            service_entities: s.service_entities || [],
          }));
          setServices(orderServices);
          setExpandedServiceIndices(new Set(orderServices.map((_, i) => i)));
          setPassengers(
            (o.passengers || []).map((p) => ({
              id: p.id || p.passenger_id,
              passenger_id: p.passenger_id || p.id,
              name: p.full_name || p.name || "",
              full_name: p.full_name || p.name || "",
              nationality: p.nationality || "",
              passport_number: p.passport_number || "",
              passport_expiry: p.passport_expiry
                ? p.passport_expiry.slice(0, 10)
                : "",
              date_of_birth: p.date_of_birth
                ? p.date_of_birth.slice(0, 10)
                : "",
              gender: p.gender || "",
              sort_order: p.sort_order != null ? p.sort_order : 0,
            })),
          );
        })
        .catch((err) => {
          console.error(err);
          setError(err.message || "Failed to load order");
        });
    }
  }, [isEdit, isInvoiceForm, orderId, serviceTypes.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "vat_percentage" || name === "discount") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? 0 : parseFloat(value) || 0,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentDetailChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      payment_details: { ...prev.payment_details, [key]: value },
    }));
  };

  const handleServiceChange = (index, field, value) => {
    const updated = [...services];
    if (field === "service_type") {
      const st = serviceTypes.find((t) => t.id === value);
      updated[index] = {
        ...updated[index],
        service_type: value,
        name: st ? st.label : updated[index].name,
        vendor_id: "",
        vendor_name: "",
        extra_fields: {},
      };
    } else if (field === "vendor") {
      const [vendorId, vendorName] = (value || "").split("\0");
      updated[index] = {
        ...updated[index],
        vendor_id: vendorId || "",
        vendor_name: vendorName || "",
      };
    } else if (field === "extra_fields") {
      updated[index] = {
        ...updated[index],
        extra_fields: { ...updated[index].extra_fields, ...value },
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setServices(updated);
  };

  const syncServiceVendorTotals = (service) => {
    const vendors = Array.isArray(service.vendors) ? service.vendors : [];
    if (!vendors.length) return service;
    return {
      ...service,
      cost: vendors.reduce((sum, vendor) => sum + (Number(vendor.buy_price) || 0), 0),
      sale_amount: vendors.reduce((sum, vendor) => sum + (Number(vendor.sell_price) || 0), 0),
    };
  };

  const addServiceVendor = (serviceIndex) => {
    setServices((prev) => {
      const next = [...prev];
      const current = next[serviceIndex];
      const vendorsList = Array.isArray(current.vendors) ? current.vendors : [];
      const isFirst = vendorsList.length === 0;
      const newRow = isFirst
        ? {
            vendor_id: current.vendor_id || "",
            vendor_name: current.vendor_name || "",
            buy_price:
              current.cost !== "" && current.cost != null ? current.cost : "",
            sell_price:
              current.sale_amount !== "" && current.sale_amount != null
                ? current.sale_amount
                : "",
            extra_fields: { ...(current.extra_fields || {}) },
            vat_applicable: current.vat_applicable !== false,
          }
        : {
            vendor_id: "",
            vendor_name: "",
            buy_price: "",
            sell_price: "",
            extra_fields: {},
            vat_applicable: true,
          };
      const merged = {
        ...current,
        vendors: [...vendorsList, newRow],
      };
      const cleared = isFirst
        ? {
            ...merged,
            vendor_id: "",
            vendor_name: "",
            cost: "",
            sale_amount: "",
            extra_fields: {},
          }
        : merged;
      next[serviceIndex] = syncServiceVendorTotals(cleared);
      return next;
    });
  };

  const updateServiceVendor = (serviceIndex, vendorIndex, field, value) => {
    setServices((prev) => {
      const next = [...prev];
      const current = next[serviceIndex];
      const vendorsList = [...(current.vendors || [])];
      const vendor = { ...(vendorsList[vendorIndex] || {}) };
      if (field === "vendor_id") {
        const selectedVendor = vendors.find((v) => v.id === value);
        vendor.vendor_id = value;
        vendor.vendor_name = selectedVendor?.name || "";
      } else {
        vendor[field] = value;
      }
      vendorsList[vendorIndex] = vendor;
      next[serviceIndex] = syncServiceVendorTotals({
        ...current,
        vendors: vendorsList,
      });
      return next;
    });
  };

  const removeServiceVendor = (serviceIndex, vendorIndex) => {
    setServices((prev) => {
      const next = [...prev];
      const current = next[serviceIndex];
      const vendorsList = [...(current.vendors || [])];
      vendorsList.splice(vendorIndex, 1);
      next[serviceIndex] = syncServiceVendorTotals({
        ...current,
        vendors: vendorsList,
      });
      return next;
    });
  };

  const setExtraField = (serviceIndex, key, value) => {
    const updated = [...services];
    updated[serviceIndex] = {
      ...updated[serviceIndex],
      extra_fields: { ...updated[serviceIndex].extra_fields, [key]: value },
    };
    setServices(updated);
  };

  const setVendorExtraField = (serviceIndex, vendorIndex, key, value) => {
    setServices((prev) => {
      const next = [...prev];
      const current = next[serviceIndex];
      const vendorsList = [...(current.vendors || [])];
      const existing = vendorsList[vendorIndex] || {};
      vendorsList[vendorIndex] = {
        ...existing,
        extra_fields: { ...(existing.extra_fields || {}), [key]: value },
      };
      next[serviceIndex] = { ...current, vendors: vendorsList };
      return next;
    });
  };

  const newPassengerId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "";

  const addPassenger = () => {
    const id = newPassengerId();
    setPassengers((prev) => [
      ...prev,
      {
        id: id || `new-${Date.now()}-${prev.length}`,
        name: "",
        full_name: "",
        nationality: "",
        passport_number: "",
        passport_expiry: "",
        date_of_birth: "",
        gender: "",
      },
    ]);
  };

  const updatePassenger = (index, field, value) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removePassenger = (index) => {
    setPassengers((prev) => prev.filter((_, i) => i !== index));
  };

  const setServicePassengerAssignment = (serviceIndex, passengerId, role) => {
    const updated = [...services];
    let assignments = [...(updated[serviceIndex].passenger_assignments || [])];
    const idx = assignments.findIndex((a) => a.passenger_id === passengerId);
    if (idx >= 0) {
      if (role == null) {
        assignments = assignments.filter((a) => a.passenger_id !== passengerId);
      } else {
        assignments[idx] = { ...assignments[idx], role };
      }
    } else if (role != null) {
      assignments.push({ passenger_id: passengerId, role: role || "adult" });
    }
    updated[serviceIndex] = {
      ...updated[serviceIndex],
      passenger_assignments: assignments,
    };
    // Sync passenger names to annexure fields (passenger_names, applicant_names, guest_names) so one selection drives both PAX and names
    const st = serviceTypes.find(
      (t) => t.id === updated[serviceIndex].service_type,
    );
    const nameKeys = (st?.fields || [])
      .filter(
        (f) =>
          f.recurring &&
          (f.key === "passenger_names" ||
            f.key === "applicant_names" ||
            f.key === "guest_names"),
      )
      .map((f) => f.key);
    if (nameKeys.length > 0 && passengers.length > 0) {
      const names = assignments
        .map((a) => {
          const p = passengers.find(
            (pax) => (pax.passenger_id || pax.id) === a.passenger_id,
          );
          return (p?.full_name || p?.name || "").trim();
        })
        .filter(Boolean);
      const namesStr = names.join(", ");
      const extra = { ...(updated[serviceIndex].extra_fields || {}) };
      nameKeys.forEach((key) => {
        extra[key] = namesStr;
      });
      updated[serviceIndex] = { ...updated[serviceIndex], extra_fields: extra };
    }
    setServices(updated);
  };

  const toggleServicePassenger = (
    serviceIndex,
    passengerId,
    role = "adult",
  ) => {
    const assignments = services[serviceIndex]?.passenger_assignments || [];
    const existing = assignments.find((a) => a.passenger_id === passengerId);
    if (existing) {
      setServicePassengerAssignment(serviceIndex, passengerId, null);
    } else {
      setServicePassengerAssignment(serviceIndex, passengerId, role);
    }
  };

  const addService = () => {
    const newIndex = services.length;
    setServices([
      ...services,
      {
        service_type: "",
        name: "",
        vendor_id: "",
        vendor_name: "",
        cost: "",
        sale_amount: "",
        vendors: [],
        vat_applicable: true,
        extra_fields: {},
        passenger_assignments: [],
      },
    ]);
    setExpandedServiceIndices((prev) => new Set([...prev, newIndex]));
  };

  const vendorsForCategory = (category) =>
    category
      ? vendors.filter((v) => {
          const categories =
            Array.isArray(v.categories) && v.categories.length
              ? v.categories
              : v.category
                ? String(v.category)
                    .split(",")
                    .map((c) => c.trim())
                    .filter(Boolean)
                : [];
          return categories.includes(category);
        })
      : [];

  const removeService = (index) => {
    if (services.length > 1) {
      setServices(services.filter((_, i) => i !== index));
      setExpandedServiceIndices((prev) => {
        const next = new Set();
        prev.forEach((i) => {
          if (i < index) next.add(i);
          if (i > index) next.add(i - 1);
        });
        return next;
      });
    }
  };

  const toggleServiceAccordion = (index) => {
    setExpandedServiceIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };
  const expandAllServices = () =>
    setExpandedServiceIndices(new Set(services.map((_, i) => i)));
  const collapseAllServices = () => setExpandedServiceIndices(new Set());

  const SERVICE_ACCORDION_COLORS = [
    {
      bg: "bg-stone-50",
      border: "border-stone-200",
      header: "bg-stone-100",
      text: "text-stone-800",
    },
    {
      bg: "bg-slate-50",
      border: "border-slate-200",
      header: "bg-slate-100",
      text: "text-slate-800",
    },
    {
      bg: "bg-amber-50",
      border: "border-amber-200",
      header: "bg-amber-100",
      text: "text-amber-800",
    },
    {
      bg: "bg-sky-50",
      border: "border-sky-200",
      header: "bg-sky-100",
      text: "text-sky-800",
    },
    {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      header: "bg-emerald-100",
      text: "text-emerald-800",
    },
    {
      bg: "bg-violet-50",
      border: "border-violet-200",
      header: "bg-violet-100",
      text: "text-violet-800",
    },
  ];
  const getServiceAccordionStyle = (index) =>
    SERVICE_ACCORDION_COLORS[index % SERVICE_ACCORDION_COLORS.length];

  const addServiceEntityLocal = (serviceIndex, entity) => {
    setServices((prev) => {
      const next = [...prev];
      next[serviceIndex] = {
        ...next[serviceIndex],
        service_entities: [
          ...(next[serviceIndex].service_entities || []),
          entity,
        ],
      };
      return next;
    });
  };

  const removeServiceEntityLocal = (serviceIndex, entityId) => {
    setServices((prev) => {
      const next = [...prev];
      next[serviceIndex] = {
        ...next[serviceIndex],
        service_entities: (next[serviceIndex].service_entities || []).filter(
          (e) => e.id !== entityId,
        ),
      };
      return next;
    });
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    const nameErr = validateName(newCustomerForm.name, { required: true });
    const emailErr = validateEmail(newCustomerForm.email, { required: false });
    if (nameErr || emailErr) {
      setError(nameErr || emailErr);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const created = await api.customers.create(newCustomerForm);
      setCustomers((prev) => [created, ...prev]);
      setFormData((prev) => ({ ...prev, customer_id: created.id }));
      setShowAddCustomer(false);
      setNewCustomerForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        company: "",
        opening_balance: "",
      });
    } catch (err) {
      setError(err.message || "Failed to add customer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.customer_id) {
      setError("Please select a customer");
      return;
    }
    const dueErr = validateDueDate(formData.due_date);
    if (dueErr) {
      setError(dueErr);
      return;
    }
    const lineSale = (s) => {
      const vL = s.vendors || [];
      if (vL.length) {
        return vL.reduce((sum, v) => sum + (Number(v.sell_price) || 0), 0);
      }
      return Number(s.sale_amount) || 0;
    };
    const validServices = services.filter(
      (s) => s.service_type && s.name?.trim() && lineSale(s) >= 0,
    );
    if (validServices.length === 0) {
      setError("Add at least one service with type, name and sale amount");
      return;
    }
    for (const s of validServices) {
      const mergedVendorFields = (s.vendors || []).reduce(
        (acc, v) => ({ ...acc, ...(v.extra_fields || {}) }),
        {},
      );
      const ef = { ...mergedVendorFields, ...(s.extra_fields || {}) };
      const fromKey = Object.keys(ef).find((k) =>
        /from_date|start_date|travel_from/i.test(k),
      );
      const toKey = Object.keys(ef).find((k) =>
        /to_date|end_date|travel_to/i.test(k),
      );
      if (fromKey && toKey && ef[fromKey] && ef[toKey]) {
        const rangeErr = validateDateRange(ef[fromKey], ef[toKey]);
        if (rangeErr) {
          setError(`Service "${s.name || "line"}": ${rangeErr}`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      if (isInvoiceForm) {
        const toInvSvc = (s) => {
          const vL = s.vendors || [];
          const amount = vL.length
            ? vL.reduce((sum, v) => sum + (Number(v.sell_price) || 0), 0)
            : Number(s.sale_amount) || 0;
          const cost = vL.length
            ? vL.reduce((sum, v) => sum + (Number(v.buy_price) || 0), 0)
            : Number(s.cost) || 0;
          return {
            name: s.name.trim(),
            description: (s.description && String(s.description).trim()) || undefined,
            service_type: s.service_type || null,
            amount,
            cost: cost || undefined,
            notes: (s.notes && String(s.notes).trim()) || undefined,
            extra_fields:
              s.extra_fields && Object.keys(s.extra_fields).length
                ? s.extra_fields
                : undefined,
            passenger_assignments: Array.isArray(s.passenger_assignments)
              ? s.passenger_assignments.map((p) => ({
                  passenger_id: p.passenger_id || p.id,
                  role: p.role,
                  full_name: (p.full_name || p.name || "").trim() || undefined,
                }))
              : undefined,
            service_entities: Array.isArray(s.service_entities)
              ? s.service_entities.map((e) => ({
                  id: e.id,
                  entity_type: e.entity_type,
                  reference_id: e.reference_id,
                  name: e.name,
                  description: e.description,
                  details: e.details,
                }))
              : undefined,
            vendors: vL
              .filter(
                (v) =>
                  v.vendor_id ||
                  v.vendor_name ||
                  Number(v.buy_price) ||
                  Number(v.sell_price),
              )
              .map((v) => ({
                vendor_id: v.vendor_id || undefined,
                vendor_name: v.vendor_name || undefined,
                buy_price: Number(v.buy_price) || 0,
                sell_price: Number(v.sell_price) || 0,
                extra_fields:
                  v.extra_fields && Object.keys(v.extra_fields).length
                    ? v.extra_fields
                    : undefined,
              })),
          };
        };
        const inv = await api.invoices.create({
          customer_id: formData.customer_id,
          services: validServices.map(toInvSvc),
          vat_percentage: formData.vat_percentage ?? 5,
          discount: formData.discount ?? 0,
          currency: formData.currency,
          due_date: formData.due_date || null,
          notes: formData.notes || null,
          ...(parentInvoiceId ? { parent_invoice_id: parentInvoiceId } : {}),
        });
        navigate(`/invoices/${inv.id}`);
        return;
      }
      const nameOrFull = (p) => (p.full_name || p.name || "").trim();
      const passengerPayload = passengers
        .filter((p) => nameOrFull(p))
        .map((p, i) => {
          const pid = p.passenger_id || p.id;
          const isUuid = pid && /^[a-f0-9-]{36}$/i.test(pid);
          if (isUuid && p.passenger_id) {
            return { passenger_id: pid, sort_order: i };
          }
          return {
            id: isUuid ? pid : p.id || newPassengerId(),
            full_name: nameOrFull(p),
            nationality: p.nationality?.trim() || undefined,
            passport_number: p.passport_number?.trim() || undefined,
            passport_expiry: p.passport_expiry || undefined,
            date_of_birth: p.date_of_birth || undefined,
            gender: p.gender || undefined,
            sort_order: i,
          };
        });
      const payload = {
        payment_type: formData.payment_type || undefined,
        payment_details: Object.keys(formData.payment_details || {}).length
          ? formData.payment_details
          : undefined,
        currency: formData.currency,
        due_date: formData.due_date || undefined,
        notes: formData.notes || undefined,
        services: validServices.map((s) => ({
          service_type: s.service_type,
          name: s.name,
          vendor_id: s.vendor_id || undefined,
          vendor_name: s.vendor_name || undefined,
          cost: Number(s.cost) || 0,
          sale_amount: Number(s.sale_amount),
          vendors: (s.vendors || [])
            .filter(
              (vendor) =>
                vendor.vendor_id ||
                vendor.vendor_name ||
                Number(vendor.buy_price) ||
                Number(vendor.sell_price),
            )
            .map((vendor) => ({
              vendor_id: vendor.vendor_id || undefined,
              vendor_name: vendor.vendor_name || undefined,
              buy_price: Number(vendor.buy_price) || 0,
              sell_price: Number(vendor.sell_price) || 0,
              extra_fields:
                vendor.extra_fields && Object.keys(vendor.extra_fields).length
                  ? vendor.extra_fields
                  : undefined,
            })),
          vat_applicable: (s.vendors || []).length
            ? (s.vendors || []).some((v) => v.vat_applicable !== false)
            : s.vat_applicable !== false,
          extra_fields:
            s.extra_fields && Object.keys(s.extra_fields).length
              ? s.extra_fields
              : undefined,
          passenger_assignments: (s.passenger_assignments || []).length
            ? s.passenger_assignments.filter((a) => a.passenger_id)
            : undefined,
        })),
        passengers: passengerPayload.length ? passengerPayload : undefined,
      };
      if (isEdit) {
        await api.orders.update(orderId, payload);
        navigate(`/orders/${orderId}`);
      } else {
        payload.customer_id = formData.customer_id;
        await api.orders.create(payload);
        navigate("/orders");
      }
    } catch (err) {
      setError(err.message || "Failed to save order");
    } finally {
      setSubmitting(false);
    }
  };

  const paymentFields = PAYMENT_EXTRA_FIELDS[formData.payment_type] || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(isInvoiceForm ? "/invoices" : "/orders")}
          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          title={isInvoiceForm ? "Back to Invoices" : "Back to Orders"}
        >
          <ArrowLeft size={20} className="text-stone-600" />
        </button>
        <h1 className="text-2xl font-heading font-semibold text-secondary">
          {isInvoiceForm
            ? parentInvoiceId
              ? "New Add-on Invoice"
              : "New Invoice"
            : isEdit
              ? "Edit Order"
              : "New Order"}
        </h1>
      </div>

      {!isInvoiceForm && (
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3 text-sm text-cyan-800">
        <strong>What is an Order?</strong> An order is a quote or booking
        request with services and prices. When the customer confirms, you close
        the order and create an <strong>Invoice</strong> for payment. Invoice =
        the bill you send to the customer.
      </div>
      )}
      {isInvoiceForm && parentInvoiceId && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3 text-cyan-800 text-sm">
          This add-on is linked to a parent invoice. Service lines match{" "}
          <strong>New Order</strong> (type, name, and per-vendor fields).
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {showAddCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-secondary mb-4">
              Add new customer
            </h3>
            <form onSubmit={handleAddCustomer} className="space-y-3">
              <input
                type="text"
                placeholder="Name *"
                value={newCustomerForm.name}
                onChange={(e) =>
                  setNewCustomerForm((p) => ({ ...p, name: e.target.value }))
                }
                onKeyDown={onlyNameInput}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={newCustomerForm.email}
                onChange={(e) =>
                  setNewCustomerForm((p) => ({ ...p, email: e.target.value }))
                }
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="Phone"
                value={newCustomerForm.phone}
                onChange={(e) =>
                  setNewCustomerForm((p) => ({ ...p, phone: e.target.value }))
                }
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
              />
              <div>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Opening balance (e.g. 100 or -100)"
                  value={newCustomerForm.opening_balance}
                  onChange={(e) =>
                    setNewCustomerForm((p) => ({
                      ...p,
                      opening_balance: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                />
                <p className="form-helper mt-1">
                  Positive means amount to receive from customer. Negative means customer credit.
                </p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddCustomer(false)}
                  className="px-4 py-2 border border-stone-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
                >
                  Save & Select
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-secondary mb-4">
              Add new vendor
            </h3>
            {addVendorError && (
              <p className="text-sm text-red-600 mb-3">{addVendorError}</p>
            )}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newVendorForm.name.trim()) return;
                setAddVendorError("");
                try {
                  const created = await api.vendors.create({
                    name: newVendorForm.name.trim(),
                    category: newVendorForm.category,
                    opening_balance: newVendorForm.opening_balance,
                  });
                  setVendors((prev) => [created, ...prev]);
                  if (vendorForServiceIndex != null) {
                    handleServiceChange(
                      vendorForServiceIndex,
                      "vendor",
                      `${created.id}\0${created.name}`,
                    );
                  }
                  if (
                    vendorForServiceVendorLine &&
                    vendorForServiceVendorLine.serviceIndex != null &&
                    vendorForServiceVendorLine.vendorIndex != null
                  ) {
                    updateServiceVendor(
                      vendorForServiceVendorLine.serviceIndex,
                      vendorForServiceVendorLine.vendorIndex,
                      "vendor_id",
                      created.id,
                    );
                  }
                  setShowAddVendor(false);
                  setVendorForServiceIndex(null);
                  setVendorForServiceVendorLine(null);
                  setNewVendorForm({
                    name: "",
                    category: "other",
                    opening_balance: "",
                  });
                } catch (err) {
                  setAddVendorError(
                    err.message ||
                      "Failed to add vendor. Try again or ask an admin to add the vendor.",
                  );
                }
              }}
              className="space-y-3"
            >
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Vendor name *
                </label>
                <input
                  type="text"
                  value={newVendorForm.name}
                  onChange={(e) =>
                    setNewVendorForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="e.g. City Travel Agency"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Category
                </label>
                <select
                  value={newVendorForm.category}
                  onChange={(e) =>
                    setNewVendorForm((p) => ({
                      ...p,
                      category: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                >
                  {VENDOR_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Opening balance
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newVendorForm.opening_balance}
                  onChange={(e) =>
                    setNewVendorForm((p) => ({
                      ...p,
                      opening_balance: e.target.value,
                    }))
                  }
                  placeholder="e.g. 100 or -100"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                />
                <p className="form-helper mt-1">
                  Positive means payable to vendor. Negative (example -100) means vendor credit balance.
                </p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddVendor(false);
                    setVendorForServiceIndex(null);
                    setVendorForServiceVendorLine(null);
                    setAddVendorError("");
                  }}
                  className="px-4 py-2 border border-stone-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
                >
                  Save & Select
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 min-w-0 max-w-full">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 sm:p-6 space-y-6 min-w-0 max-w-full">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleInputChange}
                required
                disabled={isEdit || (isInvoiceForm && !!parentInvoiceId)}
                className="flex-1 px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:bg-stone-100 disabled:cursor-not-allowed"
              >
                <option value="">Select a customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.email ? `(${c.email})` : ""}
                  </option>
                ))}
              </select>
              {!isEdit && (
                <button
                  type="button"
                  onClick={() => setShowAddCustomer(true)}
                  className="px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10"
                >
                  Add new
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Currency
              </label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} - {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Payment Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                min={toLocalDateInputValue()}
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              />
            </div>
          </div>

          {isInvoiceForm && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  VAT percentage
                </label>
                <input
                  type="number"
                  name="vat_percentage"
                  value={formData.vat_percentage}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Discount ({formData.currency})
                </label>
                <input
                  type="number"
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
              </div>
            </div>
          )}

          {!isInvoiceForm && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Payment type
            </label>
            <select
              name="payment_type"
              value={formData.payment_type}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            >
              <option value="">Select payment type</option>
              {paymentTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            {paymentFields.length > 0 && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {paymentFields.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs text-stone-600 mb-1">
                      {f.label}
                    </label>
                    <input
                      type={f.type}
                      value={formData.payment_details?.[f.key] || ""}
                      onChange={(e) =>
                        handlePaymentDetailChange(f.key, e.target.value)
                      }
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Services first: select type then related annexure fields appear */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-stone-700">
                Services <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={addService}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg"
              >
                <Plus size={16} />
                Add service
              </button>
            </div>
            <p className="form-helper mb-3 max-w-full">
              First select a <strong>Service type</strong> for each line; then
              the related fields (e.g. Travel Date, Passenger names) appear
              below. Expand only the service you are editing or expand all.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                onClick={expandAllServices}
                className="text-xs font-medium text-primary hover:bg-primary/10 px-2.5 py-1.5 rounded-lg border border-primary/30"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={collapseAllServices}
                className="text-xs font-medium text-stone-600 hover:bg-stone-100 px-2.5 py-1.5 rounded-lg border border-stone-200"
              >
                Collapse all
              </button>
            </div>
            <div className="space-y-3">
              {services.map((svc, index) => {
                const style = getServiceAccordionStyle(index);
                const isExpanded = expandedServiceIndices.has(index);
                const typeLabel =
                  serviceTypes.find((t) => t.id === svc.service_type)?.label ||
                  "Service";
                const title = svc.name?.trim() || typeLabel;
                const hasV = (svc.vendors || []).length > 0;
                return (
                  <div
                    key={index}
                    className={`rounded-xl border-2 overflow-hidden ${style.bg} ${style.border}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleServiceAccordion(index)}
                      className={`w-full flex items-center justify-between gap-2 px-4 py-3 ${style.header} ${style.text} font-medium text-left hover:opacity-90 transition-opacity`}
                    >
                      <span className="truncate">
                        Service {index + 1}: {title}
                      </span>
                      {isExpanded ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="p-4 space-y-3">
                        <div className="flex gap-3 items-start flex-wrap min-w-0">
                          <div className="flex-1 min-w-0 sm:min-w-[180px]">
                            <label className="block text-xs text-stone-600 mb-1">
                              Service type
                            </label>
                            <select
                              value={svc.service_type}
                              onChange={(e) =>
                                handleServiceChange(
                                  index,
                                  "service_type",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                            >
                              <option value="">Select type</option>
                              {serviceTypes.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1 min-w-0 sm:min-w-[120px]">
                            <label className="block text-xs text-stone-600 mb-1">
                              Name
                            </label>
                            <input
                              type="text"
                              value={svc.name}
                              onChange={(e) =>
                                handleServiceChange(
                                  index,
                                  "name",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. Dubai–Muscat flight"
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                            />
                          </div>
                          {!hasV && svc.service_type &&
                            (() => {
                              const category =
                                serviceTypeVendorCategory[svc.service_type];
                              const lineVendors = vendorsForCategory(category);
                              return (
                                <div className="flex-1 min-w-0 sm:min-w-[160px]">
                                  <label className="block text-xs text-stone-600 mb-1">
                                    Vendor
                                  </label>
                                  <div className="flex gap-1 min-w-0">
                                    <select
                                      value={svc.vendor_id || "__none__"}
                                      onChange={(e) => {
                                        const id = e.target.value;
                                        if (!id || id === "__none__") {
                                          handleServiceChange(
                                            index,
                                            "vendor",
                                            "",
                                          );
                                          return;
                                        }
                                        if (id === "__add__") {
                                          setVendorForServiceIndex(index);
                                          setNewVendorForm({
                                            name: "",
                                            category:
                                              serviceTypeVendorCategory[
                                                svc.service_type
                                              ] || "other",
                                            opening_balance: "",
                                          });
                                          setShowAddVendor(true);
                                          return;
                                        }
                                        const v = vendors.find(
                                          (x) => x.id === id,
                                        );
                                        handleServiceChange(
                                          index,
                                          "vendor",
                                          `${id}\0${(v && v.name) || ""}`,
                                        );
                                      }}
                                      className="flex-1 px-3 py-2 border border-stone-300 rounded-lg"
                                    >
                                      <option value="__none__">
                                        — None / External —
                                      </option>
                                      {lineVendors.map((v) => (
                                        <option key={v.id} value={v.id}>
                                          {v.name}
                                        </option>
                                      ))}
                                      <option value="__add__">
                                        + Add vendor
                                      </option>
                                    </select>
                                    {/* <p className="form-helper mt-0.5">Not in list? Select «+ Add vendor» to add one.</p> */}
                                  </div>
                                </div>
                              );
                            })()}
                          {!hasV && (
                          <div className="w-24">
                            <label className="block text-xs text-stone-600 mb-1">
                              Cost
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={svc.cost}
                              onChange={(e) =>
                                handleServiceChange(
                                  index,
                                  "cost",
                                  e.target.value,
                                )
                              }
                              onKeyDown={(e) => onlyNumericInput(e, true)}
                              placeholder="0"
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                            />
                          </div>
                          )}
                          {!hasV && (
                          <div className="w-28">
                            <label className="block text-xs text-stone-600 mb-1">
                              Sale <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={svc.sale_amount}
                              onChange={(e) =>
                                handleServiceChange(
                                  index,
                                  "sale_amount",
                                  e.target.value,
                                )
                              }
                              onKeyDown={(e) => onlyNumericInput(e, true)}
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                            />
                            <p className="form-helper mt-0.5">
                              Amount charged to customer
                            </p>
                          </div>
                          )}
                          <div className="w-full rounded-lg border border-stone-200 bg-stone-50 p-3">
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <p className="text-xs font-semibold text-stone-700">
                                {hasV
                                  ? "Vendors (each line: buy, sell, VAT, details below)"
                                  : "Service Vendors"}
                              </p>
                              <button
                                type="button"
                                onClick={() => addServiceVendor(index)}
                                className="text-xs font-medium text-primary hover:underline"
                              >
                                + Add vendor
                              </button>
                            </div>
                            {(svc.vendors || []).length === 0 ? (
                              <p className="text-xs text-stone-500">
                                Optional: add multiple vendors to split buy/sell prices and calculate vendor-wise profit.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {(svc.vendors || []).map((serviceVendor, vendorIndex) => {
                                  const category =
                                    serviceTypeVendorCategory[svc.service_type];
                                  const lineVendors = vendorsForCategory(category);
                                  return (
                                    <div
                                      key={`${index}-${vendorIndex}`}
                                      className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_auto] gap-2 sm:items-end"
                                    >
                                      <select
                                        value={serviceVendor.vendor_id || ""}
                                        onChange={(e) => {
                                          const nextValue = e.target.value;
                                          if (nextValue === "__add__") {
                                            setVendorForServiceIndex(index);
                                            setVendorForServiceVendorLine({
                                              serviceIndex: index,
                                              vendorIndex,
                                            });
                                            setNewVendorForm({
                                              name: "",
                                              category:
                                                serviceTypeVendorCategory[
                                                  svc.service_type
                                                ] || "other",
                                              opening_balance: "",
                                            });
                                            setShowAddVendor(true);
                                            return;
                                          }
                                          updateServiceVendor(
                                            index,
                                            vendorIndex,
                                            "vendor_id",
                                            nextValue,
                                          );
                                        }}
                                        className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                                      >
                                        <option value="">External / manual vendor</option>
                                        {lineVendors.map((v) => (
                                          <option key={v.id} value={v.id}>
                                            {v.name}
                                          </option>
                                        ))}
                                        <option value="__add__">+ Add vendor</option>
                                      </select>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={serviceVendor.buy_price}
                                        onChange={(e) =>
                                          updateServiceVendor(
                                            index,
                                            vendorIndex,
                                            "buy_price",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Buy price"
                                        className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                                      />
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={serviceVendor.sell_price}
                                        onChange={(e) =>
                                          updateServiceVendor(
                                            index,
                                            vendorIndex,
                                            "sell_price",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Sell price"
                                        className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                                      />
                                      <div className="flex items-center gap-2 sm:col-span-1">
                                        <label className="flex items-center gap-1.5 text-xs text-stone-600 whitespace-nowrap">
                                          <input
                                            type="checkbox"
                                            checked={
                                              serviceVendor.vat_applicable !== false
                                            }
                                            onChange={(e) =>
                                              updateServiceVendor(
                                                index,
                                                vendorIndex,
                                                "vat_applicable",
                                                e.target.checked,
                                              )
                                            }
                                            className="rounded"
                                          />
                                          VAT
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeServiceVendor(
                                              index,
                                              vendorIndex,
                                            )
                                          }
                                          className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="flex items-end gap-2">
                            {!hasV && (
                            <label className="flex items-center gap-2 text-sm text-stone-600">
                              <input
                                type="checkbox"
                                checked={svc.vat_applicable}
                                onChange={(e) =>
                                  handleServiceChange(
                                    index,
                                    "vat_applicable",
                                    e.target.checked,
                                  )
                                }
                                className="rounded"
                              />
                              VAT
                            </label>
                            )}
                            {services.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeService(index)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <X size={18} />
                              </button>
                            )}
                          </div>
                        </div>
                        {passengers.length > 0 && (
                          <div className="pt-2 border-t border-stone-200">
                            <label className="block text-xs text-stone-600 mb-2">
                              Passengers on this service (PAX count)
                            </label>
                            <div className="flex flex-wrap gap-3 items-center">
                              {passengers.map((p, i) => {
                                const pid = p.passenger_id || p.id;
                                const name =
                                  (p.full_name || p.name || "").trim() ||
                                  `Passenger ${i + 1}`;
                                const assignment = (
                                  svc.passenger_assignments || []
                                ).find((a) => a.passenger_id === pid);
                                const checked = !!assignment;
                                return (
                                  <div
                                    key={pid || i}
                                    className="flex items-center gap-2"
                                  >
                                    <label className="flex items-center gap-1.5 text-sm">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) =>
                                          toggleServicePassenger(
                                            index,
                                            pid,
                                            e.target.checked ? "adult" : null,
                                          )
                                        }
                                        className="rounded"
                                      />
                                      <span className="text-stone-700">
                                        {name}
                                      </span>
                                    </label>
                                    {checked && (
                                      <select
                                        value={assignment?.role || "adult"}
                                        onChange={(e) =>
                                          setServicePassengerAssignment(
                                            index,
                                            pid,
                                            e.target.value,
                                          )
                                        }
                                        className="text-xs px-2 py-1 border border-stone-300 rounded"
                                      >
                                        <option value="adult">Adult</option>
                                        <option value="child">Child</option>
                                        <option value="infant">Infant</option>
                                      </select>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {(svc.passenger_assignments || []).length > 0 && (
                              <p className="form-helper mt-1">
                                PAX count: {svc.passenger_assignments.length}
                              </p>
                            )}
                          </div>
                        )}
                        {isEdit && orderId && svc.id && (
                          <ServiceEntitiesBlock
                            orderId={orderId}
                            serviceId={svc.id}
                            entities={svc.service_entities || []}
                            onAdd={(entity) =>
                              addServiceEntityLocal(index, entity)
                            }
                            onRemove={(entityId) =>
                              removeServiceEntityLocal(index, entityId)
                            }
                          />
                        )}
                        {svc.service_type &&
                          (() => {
                            const st = serviceTypes.find(
                              (t) => t.id === svc.service_type,
                            );
                            const fields = st?.fields || [];
                            if (fields.length === 0) return null;
                            const isRecurringNames = (f) =>
                              f.recurring &&
                              (f.key === "passenger_names" ||
                                f.key === "applicant_names" ||
                                f.key === "guest_names");
                            const vList = svc.vendors || [];
                            const fieldBlocks =
                              vList.length > 0
                                ? vList.map((vRow, vi) => ({
                                    key: `v-${vi}`,
                                    header:
                                      vList.length > 1
                                        ? (vRow.vendor_name || "").trim() ||
                                          `Vendor line ${vi + 1}`
                                        : null,
                                    get: (k) => {
                                      const ve = vRow.extra_fields?.[k];
                                      if (ve != null && ve !== "")
                                        return ve;
                                      if (vi === 0) return svc.extra_fields?.[k];
                                      return "";
                                    },
                                    set: (k, val) =>
                                      setVendorExtraField(index, vi, k, val),
                                  }))
                                : [
                                    {
                                      key: "svc",
                                      header: null,
                                      get: (k) => svc.extra_fields?.[k],
                                      set: (k, val) =>
                                        setExtraField(index, k, val),
                                    },
                                  ];
                            const firstKey = fieldBlocks[0].key;
                            return (
                              <div className="pt-2 border-t border-stone-200 space-y-2">
                                {st?.amountFieldsNote && (
                                  <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1.5 rounded border border-amber-200 break-words max-w-full">
                                    {st.amountFieldsNote}
                                  </p>
                                )}
                                {fieldBlocks.map((block) => (
                                  <div key={block.key} className="space-y-2">
                                    {block.header && (
                                      <p className="text-xs font-semibold text-stone-700 border-b border-stone-100 pb-1">
                                        {block.header}
                                      </p>
                                    )}
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                      {fields.map((f) => {
                                        if (
                                          isRecurringNames(f) &&
                                          passengers.length > 0
                                        ) {
                                          if (block.key !== firstKey) {
                                            return null;
                                          }
                                          const assignments =
                                            svc.passenger_assignments || [];
                                          const namesFromAssignment =
                                            assignments
                                              .map((a) => {
                                                const p = passengers.find(
                                                  (pax) =>
                                                    (pax.passenger_id ||
                                                      pax.id) ===
                                                    a.passenger_id,
                                                );
                                                return (
                                                  p?.full_name ||
                                                  p?.name ||
                                                  ""
                                                ).trim();
                                              })
                                              .filter(Boolean);
                                          const displayNames =
                                            namesFromAssignment.length > 0
                                              ? namesFromAssignment.join(", ")
                                              : (block.get(f.key) || "").trim();
                                          return (
                                            <div
                                              key={f.key}
                                              className="md:col-span-2 space-y-1"
                                            >
                                              <p className="form-helper">
                                                {f.label} are taken from the
                                                passengers selected above.{" "}
                                                {displayNames
                                                  ? `Names: ${displayNames}`
                                                  : "Select passengers above."}
                                              </p>
                                            </div>
                                          );
                                        }
                                        if (
                                          isRecurringNames(f) &&
                                          passengers.length === 0
                                        ) {
                                          const currentVal =
                                            block.get(f.key) || "";
                                          const valuesList = currentVal
                                            ? currentVal
                                                .split(",")
                                                .map((s) => s.trim())
                                                .filter(Boolean)
                                            : [];
                                          const setValuesList = (list) => {
                                            const cleaned = (list || [])
                                              .map((s) => String(s).trim())
                                              .filter(Boolean);
                                            block.set(
                                              f.key,
                                              cleaned.length
                                                ? cleaned.join(",")
                                                : "",
                                            );
                                          };
                                          return (
                                            <div
                                              key={f.key}
                                              className="md:col-span-2 space-y-1"
                                            >
                                              <label className="block text-xs text-stone-600 mb-1">
                                                {f.label}
                                              </label>
                                              <ChipMultiValueInput
                                                values={valuesList}
                                                onChange={setValuesList}
                                                placeholder={
                                                  f.placeholder ||
                                                  "Type names and press Enter"
                                                }
                                                helperText={f.helperText}
                                                splitBy="comma"
                                                commitOnComma
                                              />
                                            </div>
                                          );
                                        }
                                        if (f.multiple && !isRecurringNames(f)) {
                                          const raw = block.get(f.key);
                                          let values = [];
                                          if (raw != null && raw !== "") {
                                            try {
                                              const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                                              if (Array.isArray(parsed) && parsed.length > 0) {
                                                values = parsed
                                                  .map((s) => (s != null ? String(s) : ""))
                                                  .map((s) => s.trim())
                                                  .filter(Boolean);
                                              } else if (typeof raw === "string") {
                                                values = raw.split("\n").map((s) => s.trim()).filter(Boolean);
                                              }
                                            } catch (_) {
                                              values = raw.split("\n").map((s) => s.trim()).filter(Boolean);
                                            }
                                          }
                                          const saveValues = (arr) => {
                                            const cleaned = (arr || [])
                                              .map((s) => String(s).trim())
                                              .filter(Boolean);
                                            block.set(f.key, cleaned.length ? JSON.stringify(cleaned) : "");
                                          };
                                          return (
                                            <div key={f.key} className="md:col-span-2 space-y-1">
                                              <label className="block text-xs text-stone-600 mb-1">{f.label}</label>
                                              <ChipMultiValueInput
                                                values={values}
                                                onChange={saveValues}
                                                placeholder={
                                                  f.placeholder ||
                                                  "Type one value and press Enter"
                                                }
                                                helperText={f.helperText}
                                                splitBy="none"
                                                commitOnComma={false}
                                              />
                                            </div>
                                          );
                                        }
                                        const rawVal = block.get(f.key);
                                        const rawStr = typeof rawVal === "string" ? rawVal : "";
                                        const placeholderIndicatesComma = String(f.placeholder || "").includes(",");
                                        const rawLooksLikeJsonArray =
                                          rawStr.trim().startsWith("[") &&
                                          rawStr.includes("]");
                                        const shouldRenderCommaChips =
                                          f.type === "text" &&
                                          !isRecurringNames(f) &&
                                          !f.multiple &&
                                          !rawLooksLikeJsonArray &&
                                          (placeholderIndicatesComma ||
                                            (rawStr && rawStr.includes(",")));

                                        if (shouldRenderCommaChips) {
                                          const valuesList = rawStr
                                            ? rawStr
                                                .split(",")
                                                .map((s) => s.trim())
                                                .filter(Boolean)
                                            : [];
                                          const onChangeChips = (arr) => {
                                            const cleaned = (arr || [])
                                              .map((s) => String(s).trim())
                                              .filter(Boolean);
                                            block.set(
                                              f.key,
                                              cleaned.length ? cleaned.join(", ") : "",
                                            );
                                          };
                                          return (
                                            <div key={f.key} className="md:col-span-2 space-y-1">
                                              <label className="block text-xs text-stone-600 mb-1">
                                                {f.label}
                                              </label>
                                              <ChipMultiValueInput
                                                values={valuesList}
                                                onChange={onChangeChips}
                                                placeholder={f.placeholder || "Type values and press Enter"}
                                                helperText={f.helperText}
                                                splitBy="comma"
                                                commitOnComma
                                              />
                                            </div>
                                          );
                                        }
                                        const isCheckOut = f.key === "check_out_date" || f.key === "return_date";
                                        const isCheckIn = f.key === "check_in_date" || f.key === "hire_date";
                                        const startKey = f.key === "check_out_date" ? "check_in_date" : f.key === "return_date" ? "hire_date" : null;
                                        const endKey = f.key === "check_in_date" ? "check_out_date" : f.key === "hire_date" ? "return_date" : null;
                                        const startVal = startKey ? (block.get(startKey) || "").toString().trim() : "";
                                        const endVal = endKey ? (block.get(endKey) || "").toString().trim() : "";
                                        const currentDateVal = (block.get(f.key) || "").toString().trim();
                                        let dateError = "";
                                        if (f.type === "date" && currentDateVal) {
                                          if (isCheckOut && startVal && currentDateVal < startVal) {
                                            dateError = "Check-out date cannot be earlier than check-in date.";
                                            if (f.key === "return_date") dateError = "Return date cannot be earlier than hire date.";
                                          }
                                          if (isCheckIn && endVal && currentDateVal > endVal) {
                                            dateError = "Check-in date cannot be later than check-out date.";
                                            if (f.key === "hire_date") dateError = "Hire date cannot be later than return date.";
                                          }
                                        }
                                        return (
                                          <div key={f.key}>
                                            <label className="block text-xs text-stone-600 mb-1">
                                              {f.label}
                                            </label>
                                            {f.helperText && (
                                              <p className="form-helper mb-1">
                                                {f.helperText}
                                              </p>
                                            )}
                                            <input
                                              type={
                                                f.type === "date" ? "date" : "text"
                                              }
                                              value={block.get(f.key) || ""}
                                              onChange={(e) =>
                                                block.set(
                                                  f.key,
                                                  e.target.value,
                                                )
                                              }
                                              placeholder={f.placeholder || ""}
                                              className={`w-full px-2 py-1.5 text-sm border rounded-lg ${dateError ? "border-red-500 bg-red-50" : "border-stone-300"}`}
                                            />
                                            {dateError && (
                                              <p className="text-xs text-red-600 mt-1">{dateError}</p>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Passengers: add once, then assign on service lines above */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-stone-700 flex items-center gap-2">
                <User size={18} />
                Passengers
              </label>
              <button
                type="button"
                onClick={addPassenger}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg"
              >
                <Plus size={16} />
                Add passenger
              </button>
            </div>
            <p className="form-helper mb-3 max-w-full">
              Add passengers once here; then assign them on each service line
              above (e.g. Flight, Insurance) for PAX count and names.
            </p>
            {passengers.length === 0 ? (
              <p className="text-stone-500 text-sm py-2">
                No passengers added. Click &quot;Add passenger&quot; to add, or
                type names in the service fields above.
              </p>
            ) : (
              <div className="space-y-3">
                {passengers.map((p, idx) => (
                  <div
                    key={p.id || p.passenger_id || idx}
                    className="p-3 bg-stone-50 rounded-lg border border-stone-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 items-center"
                  >
                    <div>
                      <label className="block text-xs text-stone-600 mb-0.5">
                        Full name *
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. John Smith"
                        value={p.full_name ?? p.name ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          updatePassenger(idx, "full_name", v);
                          updatePassenger(idx, "name", v);
                        }}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-600 mb-0.5">
                        Nationality
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Indian, UAE"
                        value={p.nationality ?? ""}
                        onChange={(e) =>
                          updatePassenger(idx, "nationality", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-600 mb-0.5">
                        Passport number
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. A12345678"
                        value={p.passport_number ?? ""}
                        onChange={(e) =>
                          updatePassenger(
                            idx,
                            "passport_number",
                            e.target.value,
                          )
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-600 mb-0.5">
                        Passport expiry
                      </label>
                      <input
                        type="date"
                        value={p.passport_expiry ?? ""}
                        onChange={(e) =>
                          updatePassenger(
                            idx,
                            "passport_expiry",
                            e.target.value,
                          )
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                      />
                      <p className="form-helper mt-0.5">
                        Date the passport expires (dd/mm/yyyy)
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs text-stone-600 mb-0.5">
                        Date of birth
                      </label>
                      <input
                        type="date"
                        value={p.date_of_birth ?? ""}
                        onChange={(e) =>
                          updatePassenger(idx, "date_of_birth", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                      />
                      <p className="form-helper mt-0.5">
                        Passenger&apos;s birth date (dd/mm/yyyy)
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs text-stone-600 mb-0.5">
                        Gender
                      </label>
                      <select
                        value={p.gender ?? ""}
                        onChange={(e) =>
                          updatePassenger(idx, "gender", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        type="button"
                        onClick={() => removePassenger(idx)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remove passenger"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Additional notes..."
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none"
            />
          </div>
        </div>

        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => navigate("/orders")}
            className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
          >
            {submitting
              ? "Saving..."
              : isInvoiceForm
                ? "Create invoice"
                : isEdit
                  ? "Update Order"
                  : "Save Order"}
          </button>
        </div>
      </form>
    </div>
  );
}
